import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import InsightsIcon from '@mui/icons-material/Insights';
import SundargarhFilterBar from '../shared/SundargarhFilterBar';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import { buildParams } from '../shared/apiParams';

// Live-KoboToolbox counterpart of the internal /analytics page — same
// group-by statistics (duration, household members, children), but grouped
// by Enumerator / ULB / Ward instead of Enumerator / Block / GP / Village.
const API_URL = '/api/survey'; // Express backend (services/sundargarhData.js), same live-Kobo data source as the old Vercel functions
const EMPTY = { ulbs: [], wards: [], enumerators: [], startDate: '', endDate: '' };

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'ulb', label: 'By ULB', col: 'ULB' },
  { key: 'ward', label: 'By Ward', col: 'Ward' },
];

const formatDuration = (m) => {
  if (!m || m === 0) return '0 min';
  const h = Math.floor(m / 60);
  const mins = Math.floor(m % 60);
  return h > 0 ? `${h} hr ${mins} min` : `${mins} min`;
};

export default function SundargarhAnalytics() {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/sundargarh/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching Sundargarh analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    { key: 'first', label: tab.col, render: (r) => r.name || 'N/A' },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center' },
    { key: 'avgDur', label: 'Avg Duration', align: 'center', render: (r) => formatDuration(r.avgDurationMinutes) },
    { key: 'medDur', label: 'Median Duration', align: 'center', render: (r) => formatDuration(r.medianDurationMinutes) },
    { key: 'totMem', label: 'Total Members', align: 'center', render: (r) => r.totalHouseholdMembers ?? '—' },
    { key: 'avgMem', label: 'Avg Members', align: 'center', render: (r) => r.avgHouseholdMembers?.toFixed(1) ?? '—' },
    { key: 'medMem', label: 'Median Members', align: 'center', render: (r) => r.medianHouseholdMembers ?? '—' },
    { key: 'totChild', label: 'Total Children', align: 'center', render: (r) => r.totalChildrenReceiving ?? '—' },
    { key: 'avgChild', label: 'Avg Children', align: 'center', render: (r) => r.avgChildrenReceiving?.toFixed(1) ?? '—' },
    { key: 'medChild', label: 'Median Children', align: 'center', render: (r) => r.medianChildrenReceiving ?? '—' },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <InsightsIcon /> Sundargarh Analytics
        </h1>
        <p className="page-subtitle">Live group-by statistics across Enumerator, ULB and Ward, pulled directly from KoboToolbox.</p>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => {
              if (t.key !== activeTab) {
                setActiveTab(t.key);
                setPage(1);
              }
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <SundargarhFilterBar
        onApply={(v) => { setApplied(v); setPage(1); }}
        onClear={() => { setApplied(EMPTY); setPage(1); }}
        loading={loading}
      />

      <div className="card">
        <h3 className="card-title">Statistics by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>
    </div>
  );
}
